import { createSlice, PayloadAction } from '@reduxjs/toolkit';
//import cloneDeep from 'lodash/cloneDeep';

import { apiOperation, padZeros } from '@/app/api/common';
import { AppsHolder } from '@/features/apps/appsSlice';
// Type-only: checkoutApi reaches for the store singleton, and a value import
// here would close the cycle at module-init time. `import type` is erased.
import type { RoomMwCheckout } from '@/features/checkout/checkoutApi';
import { ChannelInfo, ChannelList, copyChannelList, copyChannelListDescriptors, ChannelListDescriptor, copyTagDescriptors, copyUrls, TagDescriptor, UrlInfo } from '@/features/channels/channelsSlice';
import { copyLanguageInfos, LanguageBase, LanguageInfo } from '@/features/languages/languagesSlice';
// Type-only, like checkoutApi above: projectGroupsSlice imports HotelInfo back.
import type { ProjectGroupSettings } from '@/features/projectGroups/projectGroupsSlice';

export interface HotelBase {
  id: string;
  old_id: string;
  is_new?: boolean;
  to_delete?: boolean;
}

export interface RoomBase extends HotelBase {
  old_room_no:		string;
  room_no:			string;
}

export interface FontDescriptor {
	name:			string;
	size:			string;
	color:			string;
	colorSelected:	string;
}

export interface BgDescriptor {
	normal:			string;
	selected:		string;
}

export interface FontTheme {
	normal:			FontDescriptor;
	title:			FontDescriptor;
	time:			FontDescriptor;
	date:			FontDescriptor;
	footer:			FontDescriptor;
	phone:			FontDescriptor;
	icon:			FontDescriptor;
	icon_small:		FontDescriptor;
	cast:			FontDescriptor;
}

export interface OverlaysDescriptor {
	default:		BgDescriptor;
	one:			BgDescriptor;
	two:			BgDescriptor;
	three:			BgDescriptor;
	four:			BgDescriptor;
	phone:			BgDescriptor;
	clock:			BgDescriptor;
	cast1:			BgDescriptor;
	cast2:			BgDescriptor;
	cast3:			BgDescriptor;
	cast4:			BgDescriptor;
	guestmap:		BgDescriptor;
}

export interface ThemeDescriptor {
	active:			string;
	variant:		string;
	font:			FontTheme;
	overlays:		OverlaysDescriptor;
}

export interface WelcomeDescriptor {
  enabled:			boolean;
  message:			string;
  description:		string;
  footer:			string;
  fields?:			UrlInfo[];
  theme:			ThemeDescriptor;
}

export interface MessageDescriptor {
  title:			string;
  start:			number;
  active_sec:		number;
  message:			string;
}

export interface RoomInfo {
	fields:			UrlInfo[];
}

export interface HotelRoomPublic {
  id:				string;
  room_no:			string;
  name:				string;
  channels:			ChannelInfo[];
  fields:			UrlInfo[];
  info:				RoomInfo;
  apps:				AppsHolder;
  messages:			MessageDescriptor[];
}

export interface HotelExecutives {
  partner:			string[];
  chain_manager:	string[];
  manager:			string[];
  // MW light (ticket #91) — housekeeping staff who see ONLY this project's
  // room list with a Checkout button. Optional on purpose, and the difference
  // between absent and [] matters on the wire: absent/null keeps the stored
  // list, [] CLEARS it (§3.3). Never build one with copyStrArray(undefined).
  mw_light?:		string[];
}

export interface AppsConfig {
  apps:				UrlInfo[];
}

export interface FirmwareDescriptor{
  platform?:string;
  model?:string;
  version?:string;
}

export interface OverrideFields {
  tracks?:			string;
  firmware?:     FirmwareDescriptor[];
};

// Channellist language selector — an operator-defined (name, TV language,
// channel list) choice. `key` is minted server-side and is the only thing
// that identifies a choice to a TV that has one stored; send "" on a new row.
// `order` MUST be sent as a value, not implied by array position — the
// backend differ matches slice elements by value, so a positional reorder
// produces an empty changelog and silently fails to save.
export interface LanguageChannelPair {
  key:				string;
  label:			string;
  lang:				string;
  list_id:			string;
  order:			number;
}

export interface LanguageSelectorConfig {
  enabled:			boolean;
  pairs:			LanguageChannelPair[];
}

export interface HotelLimits extends HotelBase {
  rooms:			number | null;
  channel_lists:	ChannelListDescriptor[];
  urls:				UrlInfo[];
  activation_code:	string;
  welcome:			WelcomeDescriptor;
  messages:			MessageDescriptor[];
  acls:				UrlInfo[];
  executives:		HotelExecutives;
  apps:				AppsConfig;
  overrides:		OverrideFields;
  language_selector:	LanguageSelectorConfig;
  // #91 §0.8 — the hotel's own Checkout value. `undefined` (key absent) means
  // the hotel follows the System settings switch; true / false win over it in
  // either direction. Only admins may change it.
  checkout_enabled?:	boolean;
  // #93 — project expiration, admins only (absent for everyone else). On a
  // save a missing key keeps the stored value; 0 / "" clear it.
  expires_at?:		number;		// Unix seconds; 0 = no expiration
  expired_message?:	string;		// at most 500 characters; "" = the TV default text
}

export interface RoomBase extends HotelBase {
  old_room_no: string;
  room_no: string;
}

export interface HotelRoom extends RoomBase {
  name:				string;
  channels:			ChannelList;
  channel_lists:	ChannelListDescriptor[];
  duid:				string;
  fields:			UrlInfo[];
  info:				RoomInfo;
  apps:				AppsConfig;
  messages:			MessageDescriptor[];
  overrides:		OverrideFields;
  // Absent (undefined) = inherit the project's pairs. Present, even with
  // enabled:false and an empty pairs list, replaces the project's config
  // entirely for this room — absent and explicit-off are different things.
  language_selector?:	LanguageSelectorConfig;
  deviation?:		boolean;
  created_on?:		number;
  last_auth?:		number;
  last_modified?:	number;
  last_activated?:	number;
  // §3.8 — MW light checkout state, present for ADMINS only and only when
  // there is something to show. READ-ONLY: the server ignores it in a
  // POST /rooms.json body, and copyProjectRoom deliberately does not carry it,
  // so the room the page edits never sends it back. Read it from the project
  // read as it was STORED (props.pub), never from the editable copy.
  mw_checkout?:		RoomMwCheckout | null;
}

export interface RoomManager extends HotelBase {
  connected:		number;
  rooms: 			HotelRoom[];
}

export interface PlatformVariant {
  variant:			string;
  count:			number;
}

export interface PlatformStats {
  platform:			string; 
  cumul:			number;
  variants:			PlatformVariant[];
}

export interface RoomStats {
  allocated:		number;
  activated:		number;
  connected:		number;
  platforms?:		PlatformStats[];
}

export interface HotelStats {
  rooms:				RoomStats;
  executives?:			HotelExecutives;
  // Task-80 §3.3 — what a grouped project inherits from its group, READ-ONLY.
  // Absent for an ungrouped project. Never merged into executives.
  group_settings?:		ProjectGroupSettings;
}

export interface HotelInfo extends HotelBase {
  name:				string;
  owner:			string;
  executives?:		HotelExecutives;
  languages?:	LanguageBase[];
  tags:				TagDescriptor[];
  urls:				UrlInfo[];
  // A light row (§3.1) sends `stats: null`, not an absent member — the list
  // has to render it as blank rather than as zeroes.
  stats?:			HotelStats | null;
  state?:			string;
  // §3.1 — set only on the minimal rows the UNFILTERED /hotels.json list adds
  // for projects the caller is MW light on. Such a project is never also a
  // normal row for them, carries no stats or urls, and opens /mw_light/:id
  // instead of the project page. See features/projects/mwLight.ts.
  mw_light?:		boolean;
  // #93 §3.1 / §3.3 — admins only. expires_at is on list rows only when set;
  // expired is read-only, computed by the server against its own clock, and
  // deliberately not carried by copyProjectInfo.
  expires_at?:		number;
  expired?:			boolean;
  // Task-80 §3.4 — the project group's uuid, absent when ungrouped. On a save
  // an absent key keeps the membership, "" leaves the group, "<id>" joins it.
  group?:			string;
  // The single-project read's inherited block (§3.3), read-only.
  group_settings?:	ProjectGroupSettings;
}

export interface HotelInfoPublic extends HotelInfo {
  limits:			HotelLimits;
  rooms:			RoomManager;
  languages:		LanguageInfo[];
}

export interface ProjectsHolder {
  projects:			HotelInfo[];
  stats:			RoomStats;
  active:			HotelInfoPublic | null;
  channelListProjects:	HotelInfo[] | null;
  channelListRooms:		HotelRoom[] | null;
}

export interface RoomStats {
  allocated:		number;
  activated:		number;
  connected:		number;
  created:      number;
}

export const projectsUrl = "/hotels.json";
export const limitsUrl = "/limits.json";
export const roomsUrl = "/rooms.json";

const initialState: ProjectsHolder = {
  projects:			[],
  stats: {
    allocated:		0,
    activated:		0,
    connected:		0,
    created:      0
  },
  active:			null,
  channelListProjects:	null,
  channelListRooms:		null,
}

export const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    assign: (state, action) => {
      state.projects = action.payload.projects;
      state.active = action.payload.active;
    },
    setProjects: (state, action) => {
      state.projects = action.payload;

      let stats: RoomStats = {
        allocated:	0,
        activated:	0,
        connected:	0,
        created:    0,
        platforms:	[],
      };

      const platformMap: { [key: string]: { cumul: number; variants: { [key: string]: number } } } = {};

      for(let proj of state.projects) {
        if (!proj.stats)
          continue;

        stats.allocated += proj.stats.rooms.allocated;
        stats.activated += proj.stats.rooms.activated;
        stats.connected += proj.stats.rooms.connected;
        stats.created += proj.stats.rooms.created;


        // Aggregate platforms and variants
        if (proj.stats.rooms.platforms) {
          for (const platform of proj.stats.rooms.platforms) {
            if (!platformMap[platform.platform]) {
              platformMap[platform.platform] = { cumul: 0, variants: {} };
            }
            platformMap[platform.platform].cumul += platform.cumul;

            // Aggregate variants
            if (platform.variants) {
              for (const variant of platform.variants) {
                const variantKey = variant.variant || '';
                if (platformMap[platform.platform].variants[variantKey]) {
                  platformMap[platform.platform].variants[variantKey] += variant.count;
                } else {
                  platformMap[platform.platform].variants[variantKey] = variant.count;
                }
              }
            }
          }
        }
      }

      // Convert platformMap to array with variants
      stats.platforms = Object.keys(platformMap).map(key => ({
        platform: key,
        cumul: platformMap[key].cumul,
        variants: Object.keys(platformMap[key].variants).map(variantKey => ({
          variant: variantKey,
          count: platformMap[key].variants[variantKey],
        })),
      }));

      state.stats = stats;
    },
    setActive: (state, action) => {
      state.active = action.payload;
    },
    setChannelListProjects: (state, action) => {
      state.channelListProjects = action.payload;
    },
    setChannelListRooms: (state, action) => {
      state.channelListRooms = action.payload;
    },

    patchRoomInfo: (state, action: PayloadAction<{ hotelId: string; roomNo: string; room: HotelRoom }>) => {
      const { hotelId, roomNo, room } = action.payload;
      if (!state.active || state.active.id !== hotelId || !state.active.rooms?.rooms) return;
      const idx = state.active.rooms.rooms.findIndex((r) => r.room_no === roomNo);
      if (idx < 0) return;
      const target = state.active.rooms.rooms[idx];
      target.info = room.info;
      target.last_auth = room.last_auth;
      target.last_modified = room.last_modified;
      target.last_activated = room.last_activated;
      target.created_on = room.created_on;
      target.deviation = room.deviation;
    },
  },
});

export function getProjectsUrl(project_id?: string, global?: boolean, group?: string) {
  if (	project_id &&
		project_id != ""
     )
    return projectsUrl + "?id=" + encodeURIComponent(project_id);

  // Task-80 §3.3 — an unknown group answers [], not every project.
  return	group
			?	projectsUrl + "?group=" + encodeURIComponent(group)
			:	projectsUrl;
}

export function getProjectsByPlatformUrl(platform: string): string | null {
  // An empty platform makes /hotels.json return EVERY project, not an empty
  // list — never build a URL for one.
  if (!platform)
    return null;

  return getProjectsUrl() + "?platform=" + encodeURIComponent(platform);
}

export function parseProjectsByPlatformResult(result: any): HotelInfo[] {
  // /hotels.json answers with a BARE ARRAY — unlike /channels.json?epg_id=
  // and /channel_lists.json?epg_id=, which wrap theirs in { projects: [...] }.
  return Array.isArray(result) ? (result as HotelInfo[]) : [];
}

export function getPlatformDeviceCount(proj: HotelInfo, platform: string): number {
  // Devices report free-form platform strings, so one project can carry both
  // "webos" and "WebOS" entries — match case-insensitively and sum them, the
  // way the server-side filter matches.
  const want = platform.toLowerCase();
  let count = 0;
  for (const p of proj.stats?.rooms?.platforms || []) {
    if ((p.platform || "").toLowerCase() === want)
      count += p.cumul;
  }
  return count;
}

export function getProjectById(id: string, projects: HotelInfo[]): HotelInfo | null {
  for(let project of projects) {
    if (project.id != id)
      continue;
    
    return project;
  }
  
  return null;
}

export function getProjectRoom(project: HotelInfoPublic, room_no: string): HotelRoom | null {
  if (!project.rooms)
    return null;
  
  for (const room of project.rooms.rooms) {
    if (room.room_no != room_no)
      continue;
      
    return room;
  }
  
  return null;
}

export function copyProjectInfo(proj: HotelInfo): HotelInfo {
  return {
    id:					proj.id,
    old_id:				proj.old_id,
    name:				proj.name,
    owner:				proj.owner,
    languages:		proj.languages
    					?	proj.languages.map(l => ({ code: l.code, priority: l.priority }))
    					:	undefined,
    tags:				copyTagDescriptors(proj.tags),
    urls:				copyUrls(proj.urls),
    stats:				proj.stats
    					?	{  rooms: {
    					         allocated:		proj.stats.rooms.allocated,
                                 activated:		proj.stats.rooms.activated,
                                 connected:		proj.stats.rooms.connected,
                                 created:     proj.stats.rooms.created
    					       },
    						}
    					:	undefined,
    is_new:				proj.is_new,
    to_delete:			proj.to_delete,
    state:				proj.state,
    // Task-80 §2.3 — carried as it is, so a save keeps the membership (an
    // absent key is "unchanged" too). Only the group dropdown sets "".
    group:				proj.group,
    //stats:				copyStats(proj.stats),
  };
}

export function copyFontDescriptor(fd: FontDescriptor): FontDescriptor {
  return {
    name:				fd.name,
	size:				fd.size,
	color:				fd.color,
	colorSelected:		fd.colorSelected,
  };
}

export function copyFontTheme(ft: FontTheme): FontTheme {
  return {
    normal:			copyFontDescriptor(ft.normal),
	title:			copyFontDescriptor(ft.title),
	time:			copyFontDescriptor(ft.time),
	date:			copyFontDescriptor(ft.date),
	footer:			copyFontDescriptor(ft.footer),
	phone:			copyFontDescriptor(ft.phone),
	icon:			copyFontDescriptor(ft.icon),
	icon_small:		copyFontDescriptor(ft.icon_small),
	cast:			copyFontDescriptor(ft.cast),
  };
}

export function copyBgDescriptor(bd: BgDescriptor): BgDescriptor {
  return {
    normal:			bd.normal,
	selected:		bd.selected,
  };
}

export function copyOverlaysDescriptor(od: OverlaysDescriptor): OverlaysDescriptor {
  return {
    default:		copyBgDescriptor(od.default),
	one:			copyBgDescriptor(od.one),
	two:			copyBgDescriptor(od.two),
	three:			copyBgDescriptor(od.three),
	four:			copyBgDescriptor(od.four),
	phone:			copyBgDescriptor(od.phone),
	clock:			copyBgDescriptor(od.clock),
	cast1:			copyBgDescriptor(od.cast1),
	cast2:			copyBgDescriptor(od.cast2),
	cast3:			copyBgDescriptor(od.cast3),
	cast4:			copyBgDescriptor(od.cast4),
	guestmap:		copyBgDescriptor(od.guestmap),
  };
}

export function copyThemeDescriptor(td: ThemeDescriptor): ThemeDescriptor {
  return {
    active:				td.active,
	variant:			td.variant,
	font:				copyFontTheme(td.font),
	overlays:			copyOverlaysDescriptor(td.overlays),
  };
}

export function copyWelcomeDescriptor(wd: WelcomeDescriptor): WelcomeDescriptor {
  return {
    enabled:			wd.enabled,
    message:			wd.message,
    description:		wd.description,
    footer:				wd.footer,
    fields:				copyUrls(wd.fields),
    theme:				copyThemeDescriptor(wd.theme),
  };
}

export function copyRoomInfo(ri: RoomInfo): RoomInfo {
  return {
    fields:				copyUrls(ri.fields),
  };
}

export function copyLanguageChannelPair(pair: LanguageChannelPair): LanguageChannelPair {
  return {
    key:				pair.key,
    label:				pair.label,
    lang:				pair.lang,
    list_id:			pair.list_id,
    order:				pair.order,
  };
}

export function copyLanguageChannelPairs(pairs: LanguageChannelPair[]): LanguageChannelPair[] {
  if (!pairs)
    return [];

  return pairs.map((pair: LanguageChannelPair) => copyLanguageChannelPair(pair));
}

// The zero value ("off", no pairs) for a project config that has never been
// saved — a limits.json written before this feature deserialises without the
// key at all, which is "off", not "unset".
export function copyLanguageSelectorConfig(cfg: LanguageSelectorConfig | null | undefined): LanguageSelectorConfig {
  if (!cfg)
    return { enabled: false, pairs: [] };

  return {
    enabled:			cfg.enabled,
    pairs:				copyLanguageChannelPairs(cfg.pairs),
  };
}

// The room-override carrier. Absent (undefined/null) means "inherit the
// project" and MUST stay absent through a copy — collapsing it to the zero
// value here would turn every room into an explicit-off override the moment
// it passes through an unrelated save. This is the one hand-enumerated room
// copy in the repo; every room mutation path routes through copyProjectRoom
// below, so a new one written by hand must carry this member too or it will
// silently strip overrides on the next unrelated room save.
export function copyRoomLanguageSelector(cfg: LanguageSelectorConfig | null | undefined): LanguageSelectorConfig | undefined {
  if (!cfg)
    return undefined;

  return copyLanguageSelectorConfig(cfg);
}

export function copyProjectRoom(room: HotelRoom): HotelRoom {
  return {
    id:					room.id,
    old_id:				room.old_id,
    old_room_no:		room.old_room_no,
    room_no:			room.room_no,
    name:				room.name,
    channels:			copyChannelList(room.channels),
    channel_lists:		copyChannelListDescriptors(room.channel_lists),
    duid:				room.duid,
    fields:				copyUrls(room.fields),
    info:				copyRoomInfo(room.info),
    apps:				copyAppsConfig(room.apps),
    messages:			copyMessageDescriptors(room.messages),
    overrides:			copyOverrideFields(room.overrides),
    language_selector:	copyRoomLanguageSelector(room.language_selector),
    deviation:			room.deviation,
    created_on:			room.created_on,
    last_auth:			room.last_auth,
    last_modified:		room.last_modified,
    last_activated:		room.last_activated,
    is_new:				room.is_new,
    to_delete:			room.to_delete,
  };
}

export function copyProjectRooms(rooms: HotelRoom[]): HotelRoom[] {
  if (!rooms)
    return [];
  
  return rooms.map((room: HotelRoom) => copyProjectRoom(room));
}

export function copyMessageDescriptors(mds: MessageDescriptor[]): MessageDescriptor[] {
  if (!mds)
    return [];
  
  return mds.map((md: MessageDescriptor) => {
    return {
      title:			md.title,
      start:			md.start,
      active_sec:		md.active_sec,
      message:			md.message,
    };
  });
}


export function copyRoomManager(rm: RoomManager): RoomManager {
  return {
    id:					rm.id,
    old_id:				rm.old_id,
    connected:			rm.connected,
    rooms:				copyProjectRooms(rm.rooms),
  };
}

export function copyStrArray(arr: string[]): string[] {
  if (!arr)
    return [];
  
  return arr.map((val: string) => val);
}

export function copyProjectExecutives(execs: HotelExecutives): HotelExecutives {
  return {
    partner:			copyStrArray(execs.partner),
    chain_manager:		copyStrArray(execs.chain_manager),
    manager:			copyStrArray(execs.manager),
    // ONLY when it is defined (§2.1). copyStrArray(undefined) returns [], and
    // [] is the wire value that CLEARS the server's list — so an unconditional
    // copy would wipe the MW light users of every project saved by a console
    // that never read the key. Absent stays absent; the server then keeps what
    // it has.
    ...(	execs.mw_light !== undefined
			?	{ mw_light: copyStrArray(execs.mw_light) }
			:	{}
		),
  };
}

export function copyAppsConfig(apps: AppsConfig): AppsConfig {
  if (	!apps ||
     	!apps.apps
     )
    apps = {
      apps:			[],
    };
  
  return {
    apps:			copyUrls(apps.apps),
  };
}

export function copyFirmwareDescriptor(firmware: FirmwareDescriptor): FirmwareDescriptor {
  return {
    platform: firmware.platform,
    model: firmware.model,
    version: firmware.version,
  };
}

export function copyFirmwareDescriptors(firmwares: FirmwareDescriptor[]): FirmwareDescriptor[] {
  if (!firmwares) return [];
  return firmwares.map((f: FirmwareDescriptor) => copyFirmwareDescriptor(f));
}

export function copyOverrideFields(overrides: OverrideFields) {
  return {
    tracks:				overrides
    					?	overrides.tracks
    					:	undefined,
    firmware:			overrides && overrides.firmware
    					?	copyFirmwareDescriptors(overrides.firmware)
    					:	undefined,
  };
}

export function copyProjectLimits(limits: HotelLimits): HotelLimits {
  return {
    id:					limits.id,
    old_id:				limits.old_id,
    rooms:				limits.rooms,
    channel_lists:		copyChannelListDescriptors(limits.channel_lists),
    urls:				copyUrls(limits.urls),
    activation_code:	limits.activation_code,
    welcome:			copyWelcomeDescriptor(limits.welcome),
    messages:			copyMessageDescriptors(limits.messages),
    acls:				copyUrls(limits.acls),
    executives:			copyProjectExecutives(limits.executives),
    apps:				copyAppsConfig(limits.apps),
    overrides:			copyOverrideFields(limits.overrides),
    language_selector:	copyLanguageSelectorConfig(limits.language_selector),
    // #91 §0.8 — ONLY when it is defined, the same rule as mw_light. Missing
    // means "follow System settings"; turning it into true/false here would
    // pin the hotel to a value on the next save.
    ...(	limits.checkout_enabled !== undefined
			?	{ checkout_enabled: limits.checkout_enabled }
			:	{}
		),
    // #93 §2.2 — the same rule. A missing key keeps the server's value while
    // 0 / "" clear it, so neither may appear unless the admin cleared it.
    ...(	limits.expires_at !== undefined
			?	{ expires_at: limits.expires_at }
			:	{}
		),
    ...(	limits.expired_message !== undefined
			?	{ expired_message: limits.expired_message }
			:	{}
		),
  };
};

export function copyProjectInfoPublic(hp: HotelInfoPublic) {
  const info = copyProjectInfo(hp);
  
  return {
    ...info,
    limits:			copyProjectLimits(hp.limits),
    rooms:			copyRoomManager(hp.rooms),
    languages:		copyLanguageInfos(hp.languages),
  }; 
}

export function getNewProject(): HotelInfo {
  return {
    id:					'',
    old_id:				'',
    name:				'',
    owner:				'',
    tags:				[],
    urls:				[],
    is_new:				true,
  };
}

export function getUnixEUTime(ts: number | undefined, seconds?: boolean) {
	if (	!ts ||
			ts <=0
	   )
	  return "-";
	
	var t = new Date(ts*1000);
	
	let sdate =	padZeros(t.getDate(), 2) + "." +
	         	padZeros(t.getMonth() + 1, 2) + "." +
	         	t.getFullYear();
	
	var stime =	padZeros(t.getHours(), 2) + ":" +
				padZeros(t.getMinutes(), 2) + ":" +
				padZeros(t.getSeconds(), 2);
	  
	return sdate + " " + stime;
}




